import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Image as ImageIcon, MapPin, Calendar, X, Upload, Eye, EyeOff } from "lucide-react";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";

const CATEGORIES = ["Head Table", "Conferences", "Workshops", "Networking", "CSR", "Awards", "Meetups", "Celebrations"];

export default function AdminGallery() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | album object

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  const refresh = async () => {
    try { const { data } = await api.get("/admin/albums"); setItems(data); } catch { /* silent */ }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-aubergine/20 border-t-aubergine rounded-full animate-spin" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const remove = async (id) => {
    if (!window.confirm("Delete this album permanently?")) return;
    try { await api.delete(`/admin/albums/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Failed"); }
  };

  const togglePublish = async (a) => {
    try { await api.patch(`/admin/albums/${a.id}`, { is_published: !a.is_published }); refresh(); }
    catch { toast.error("Failed"); }
  };

  return (
    <DashboardShell role="admin" title="Gallery Albums" subtitle="Publish curated event albums that appear on the public /gallery page">
      <div data-testid="admin-gallery" className="space-y-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-plumSoft text-sm">{items.length} album{items.length !== 1 && "s"}</p>
          <button
            data-testid="ag-new"
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-2 bg-aubergine text-ivory px-5 py-2.5 rounded-full text-[12px] uppercase tracking-widest hover:bg-aubergine/90"
          >
            <Plus className="w-4 h-4" /> New Album
          </button>
        </div>

        {items.length === 0 && (
          <div className="rounded-2xl bg-white border border-aubergine/[0.08] p-16 text-center">
            <ImageIcon className="w-10 h-10 text-plumSoft/40 mx-auto mb-3" />
            <p className="font-display text-xl text-aubergine">No albums yet</p>
            <p className="text-sm text-plumSoft mt-1">Publish your first event album — it will appear on the public gallery page.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(a => (
            <div key={a.id} className="bg-white rounded-2xl border border-aubergine/[0.08] overflow-hidden">
              <div className="aspect-[4/3] bg-lavender/50 relative">
                <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
                <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase backdrop-blur ${a.is_published ? "bg-emerald-500/20 text-emerald-100 border border-emerald-400/40" : "bg-black/40 text-white border border-white/30"}`}>
                  {a.is_published ? "Published" : "Draft"}
                </span>
                <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/85 text-[10px] tracking-widest uppercase text-aubergine">{a.category}</span>
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg text-aubergine leading-snug line-clamp-2">{a.title}</h3>
                <p className="text-[11px] text-plumSoft/80 mt-1.5 flex items-center gap-1.5"><MapPin className="w-3 h-3" />{a.location}</p>
                <p className="text-[11px] text-plumSoft/80 flex items-center gap-1.5"><Calendar className="w-3 h-3" />{a.date}</p>
                <p className="text-[11px] text-plumSoft/70 mt-2">{a.images?.length || 0} image{(a.images?.length || 0) !== 1 && "s"}</p>
                <div className="mt-4 pt-4 border-t border-aubergine/[0.06] flex items-center justify-between">
                  <div className="flex gap-1">
                    <button onClick={() => setEditing(a)} data-testid={`ag-edit-${a.id}`} className="p-2 rounded-lg text-plum hover:bg-aubergine/[0.06]" title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => togglePublish(a)} data-testid={`ag-toggle-${a.id}`} className="p-2 rounded-lg text-plum hover:bg-aubergine/[0.06]" title={a.is_published ? "Unpublish" : "Publish"}>
                      {a.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => remove(a.id)} data-testid={`ag-del-${a.id}`} className="p-2 rounded-lg text-rose hover:bg-rose/[0.06]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <AlbumEditor
            album={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
          />
        )}
      </div>
    </DashboardShell>
  );
}

function AlbumEditor({ album, onClose, onSaved }) {
  const isNew = !album;
  const [form, setForm] = useState({
    title: album?.title || "",
    category: album?.category || "Meetups",
    location: album?.location || "",
    city: album?.city || "",
    date: album?.date || new Date().toISOString().slice(0, 10),
    cover_image: album?.cover_image || "",
    description: album?.description || "",
    images: album?.images ? [...album.images] : [],
    is_published: album?.is_published ?? true,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.title.trim() || !form.location.trim() || !form.city.trim() || !form.date || !form.cover_image.trim()) {
      toast.error("Please fill title, location, city, date and cover image");
      return;
    }
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/albums", form);
      else await api.patch(`/admin/albums/${album.id}`, form);
      toast.success(isNew ? "Album created" : "Album updated");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-aubergine/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-ivory rounded-2xl w-full max-w-3xl border border-aubergine/15 shadow-2xl my-8">
        <div className="p-6 border-b border-aubergine/[0.08] flex items-center justify-between">
          <h2 className="font-display text-2xl text-aubergine">{isNew ? "New Album" : "Edit Album"}</h2>
          <button onClick={onClose} className="text-plum hover:text-aubergine p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldWrap label="Title *"><input data-testid="ae-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="input-luxe" placeholder="e.g. Hyderabad Founders Meetup" /></FieldWrap>
            <FieldWrap label="Category">
              <select data-testid="ae-category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="input-luxe">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </FieldWrap>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_180px] gap-4">
            <FieldWrap label="Venue / Location *"><input data-testid="ae-location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="input-luxe" placeholder="e.g. Taj Krishna, Banjara Hills" /></FieldWrap>
            <FieldWrap label="City *"><input data-testid="ae-city" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="input-luxe" placeholder="e.g. Hyderabad" /></FieldWrap>
            <FieldWrap label="Date *"><input data-testid="ae-date" type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="input-luxe" /></FieldWrap>
          </div>

          <FieldWrap label="Cover Image *">
            <CoverUploader value={form.cover_image} onChange={(url) => setForm({...form, cover_image: url})} />
          </FieldWrap>

          <FieldWrap label="Description (optional)">
            <textarea data-testid="ae-desc" rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-luxe" placeholder="A short description that appears on the album page" />
          </FieldWrap>

          <FieldWrap label={`Album Images (${form.images.length})`}>
            <AlbumImages images={form.images} onChange={(imgs) => setForm({...form, images: imgs})} />
          </FieldWrap>

          <label className="flex items-center gap-2 text-sm text-plum">
            <input type="checkbox" data-testid="ae-publish" checked={form.is_published} onChange={(e) => setForm({...form, is_published: e.target.checked})} className="w-4 h-4 rounded border-aubergine/30 text-aubergine focus:ring-aubergine/40" />
            Publish immediately (visible on /gallery)
          </label>
        </div>

        <div className="p-6 border-t border-aubergine/[0.08] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-[12px] uppercase tracking-widest text-plum hover:text-aubergine">Cancel</button>
          <button
            onClick={save}
            data-testid="ae-save"
            disabled={busy}
            className="inline-flex items-center gap-2 bg-aubergine text-ivory px-6 py-2.5 rounded-full text-[12px] uppercase tracking-widest hover:bg-aubergine/90 disabled:opacity-50"
          >
            {busy ? "Saving…" : (isNew ? "Create Album" : "Save Changes")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldWrap({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function CoverUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type || "")) { toast.error("Please pick an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  const previewSrc = value ? (value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : "";

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="ae-cover-file" onChange={(e) => upload(e.target.files?.[0])} />
      {previewSrc ? (
        <div className="flex items-start gap-4">
          <img src={previewSrc} alt="Cover" className="w-56 aspect-video object-cover rounded-lg border border-aubergine/8" />
          <div className="flex flex-col gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine disabled:opacity-50">
              <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Replace"}
            </button>
            <button type="button" onClick={() => onChange("")} className="text-xs text-rose inline-flex items-center gap-1 hover:opacity-80">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="w-full rounded-xl border-2 border-dashed border-aubergine/20 bg-white/60 px-5 py-8 text-sm text-plumSoft flex flex-col items-center gap-2 hover:border-aubergine/40 disabled:opacity-50">
          <Upload className="w-5 h-5 text-plum" />
          <span>{busy ? "Uploading…" : "Click to upload cover image"}</span>
          <span className="text-[10px] uppercase tracking-widest text-plumSoft/60">JPG / PNG / WEBP · up to 5 MB</span>
        </button>
      )}
    </div>
  );
}

function AlbumImages({ images, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const uploadMany = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const newImgs = [];
    for (const file of files) {
      if (!/^image\//.test(file.type || "") || file.size > 5 * 1024 * 1024) continue;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newImgs.push({ url: data.url, caption: "" });
      } catch { /* skip */ }
    }
    if (newImgs.length) {
      onChange([...images, ...newImgs]);
      toast.success(`${newImgs.length} image${newImgs.length > 1 ? "s" : ""} added`);
    }
    setBusy(false);
  };

  const updateCaption = (idx, caption) => {
    const copy = [...images];
    copy[idx] = { ...copy[idx], caption };
    onChange(copy);
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div>
      <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" data-testid="ae-imgs-file" onChange={(e) => uploadMany(Array.from(e.target.files || []))} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="mb-3 inline-flex items-center gap-2 bg-white border border-aubergine/25 text-aubergine px-4 py-2 rounded-full text-[11px] uppercase tracking-widest hover:bg-aubergine/[0.04] disabled:opacity-50">
        <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Add photos (multiple)"}
      </button>
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {images.map((img, i) => {
            const src = img.url.startsWith("http") ? img.url : `${process.env.REACT_APP_BACKEND_URL}${img.url}`;
            return (
              <div key={i} className="relative bg-white rounded-lg border border-aubergine/[0.08] overflow-hidden">
                <img src={src} alt="" className="w-full aspect-video object-cover" />
                <button type="button" onClick={() => remove(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 text-rose hover:bg-white flex items-center justify-center" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                <input value={img.caption || ""} onChange={(e) => updateCaption(i, e.target.value)} placeholder="Caption (optional)" className="w-full px-3 py-2 text-[11px] text-plum focus:outline-none border-t border-aubergine/[0.08]" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
